import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Scale, FileText, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

export const SupportServices = () => {
  const services = [
    {
      icon: Scale,
      title: "Legal & Compliance",
      description:
        "Stay compliant with Kansas rental laws while protecting your investment.",
    },
    {
      icon: FileText,
      title: "Financial Reporting",
      description:
        "Detailed monthly reports and real-time dashboard access for complete transparency.",
    },
    {
      icon: ShieldCheck,
      title: "Risk Management",
      description:
        "Comprehensive insurance coordination and liability protection for your properties.",
    },
  ];

  return (
    <section className="py-14 sm:py-18 lg:py-22">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Complete Support Services
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Beyond property management, we provide comprehensive support services
            to ensure your investment is protected and profitable.
          </p>
        </motion.div>

        {/* Desktop View - Cards */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <Card
                  data-testid="support-services-item"
                  className="h-full shadow-[0_10px_30px_rgba(15,42,74,0.06)] hover:shadow-[0_16px_45px_rgba(15,42,74,0.10)] transition-shadow"
                >
                  <CardContent className="pt-6">
                    <Icon className="h-10 w-10 text-primary mb-4" />
                    <h3 className="font-display text-lg font-semibold mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile View - Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="md:hidden"
        >
          <Accordion type="single" collapsible className="w-full">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  data-testid="support-services-item"
                >
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-primary" />
                      <span className="font-display font-semibold">
                        {service.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-muted-foreground leading-relaxed pl-8">
                      {service.description}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default SupportServices;
